import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { merge, Observable, Subject } from 'rxjs'
import { filter, finalize, first, map, share, switchMap, takeUntil, tap } from 'rxjs/operators'

import { AuthService } from '../auth.service'
import { linxioAnimations } from '@shared/animations'
import { TeamType } from '@shared/models/team.model'
import { LoginResponse } from '@app/modules/auth/auth-response.model'
import { PlatformSettings } from '@shared/models/platform-settings.model'
import { PlatformSettingsService } from '@core/services/platfrom-settings.service'
import { ApiService } from '@shared/services/api.service'
import { environment } from '@environments/environment'

@Component({
  selector: 'lin-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  animations: linxioAnimations,
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
  readonly MAINTENANCE_LOGIN_TITLE = 'MAINTENANCE_LOGIN_TITLE'
  readonly MAINTENANCE_LOGIN_DESCRIPTION = 'MAINTENANCE_LOGIN_DESCRIPTION'
  readonly version = environment.version
  readonly envName = environment.envName
  isMaintenance = false
  loginForm: FormGroup
  formError: string
  onDestroy$ = new Subject<void>()
  isSubmitting = false
  showPassword = false
  platformSettings$: Observable<PlatformSettings>
  policyLinks = { privacyPolicyLink: null, termsOfUseLink: null }

  get isSSOLogin(): boolean {
    return this.route.snapshot.routeConfig.path.includes('sso')
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private readonly platformSettingsService: PlatformSettingsService,
    private readonly apiService: ApiService,
  ) {}

  ngOnInit() {
    this.createForm()

    this.platformSettings$ = this.platformSettingsService.platformSettings$

    this.platformSettings$.pipe(filter(Boolean)).subscribe((platformSettings) => {
      this.policyLinks.privacyPolicyLink = platformSettings.privacyPolicyLink
      this.policyLinks.termsOfUseLink = platformSettings.termsOfUseLink
    })

    this.isSSOLogin && this.loginWithSSO()
  }

  createForm() {
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      isRemeberMe: '',
    })

    this.loginForm.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.onLoginFormValuesChanged()
    })

    this.loginForm.controls.isRemeberMe.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isRememberMe: boolean) => this.authService.setIsRememberMe(isRememberMe))
  }

  onLoginFormValuesChanged() {
    this.formError = ''
  }

  login() {
    this.formError = ''
    this.isSubmitting = true

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
    const login$ = this.authService.login(this.loginForm.value.email, this.loginForm.value.password, this.apiService.host).pipe(
      finalize(() => (this.isSubmitting = false)),
      share(),
    )

    const otpRequired$ = login$.pipe(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-return
      filter((data) => data['otpRequired']),
      tap((data) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.authService.setCurrentUserEmail(this.loginForm.value.email as string)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.authService.setCurrentUserPhone(data.phone as string)
        void this.router.navigate(['otp'])
      }),
    )

    const blocked$ = login$.pipe(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-return
      filter((data) => data['blocked']),
      tap((data) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
        this.authService.setCurrentUserBlockingMessage(data.message)
        void this.router.navigate(['blocked'])
      }),
    )

    const phoneIsNotVerified$ = login$.pipe(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      filter((data) => data['isPhoneVerified'] === false),
      // eslint-disable-next-line @typescript-eslint/no-misused-promises,@typescript-eslint/no-unsafe-member-access
      tap((data) => this.router.navigate(['verify-phone', data.verifyToken, { verifyPhone: true }])),
    )

    // No filter condition assumes all previous streams are synchronius !
    const loggedInSuccessfully$ = login$.pipe(
      switchMap((data: LoginResponse) =>
        this.authService.setAuth(data).pipe(
          tap(() => {
            const redirectUrl = this.redirectUrlByTeamType(data.teamType)
            void this.router.navigate([redirectUrl])
          }),
        ),
      ),
    )

    merge(otpRequired$, blocked$, phoneIsNotVerified$, loggedInSuccessfully$)
      .pipe(first())
      .subscribe(
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        () => {},
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-member-access
        (error) => (this.formError = error.error.errors[0].detail),
      )
  }

  private loginWithSSO(): void {
    let userTeamType: TeamType

    this.route.queryParams
      .pipe(
        map((params: LoginResponse) => {
          userTeamType = params.teamType
          return { ...params, loginWithId: false, otp_required: false }
        }),
        switchMap((data) => this.authService.setAuth(data)),
        tap(() => {
          const redirectUrl = this.redirectUrlByTeamType(userTeamType)
          void this.router.navigate([redirectUrl])
        }),
      )
      .subscribe()
  }

  private redirectUrlByTeamType(teamType: TeamType): string {
    switch (teamType) {
      case TeamType.Admin:
        return 'admin/devices'
      case TeamType.Client:
        return 'client'
      case TeamType.Reseller:
        return 'reseller'
    }
  }

  openLink(link: string): void {
    window.open(link, '_blank')
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}
